import { useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Spinner } from 'react-bootstrap'
import AbstractForm from '../forms/AbstractForm'
import { referenceSchema } from '../../schema/referenceSchema'
import Plus from '../../assets/svgs/plus'
import PrimaryButton from '../buttons/PrimaryButton'
import { AddButton } from './styled'
import ConsentField from './ConsentField'
import ReferenceCard from './ReferenceCard'
import { EMPTY_REFERENCE, MAX_REFERENCES } from './constants'

// Rendered inside AbstractForm's FormProvider so it can trigger validation.
function SubmitReferences({ onSubmit }) {
  const { t } = useTranslation()
  const { handleSubmit } = useFormContext()
  const [isLoading, setIsLoading] = useState(false)

  const submit = handleSubmit((data) => {
    setIsLoading(true)
    onSubmit(data, setIsLoading)
  })

  return (
    <PrimaryButton
      type="button"
      onClick={submit}
      disabled={isLoading}
      width="100%"
      style={{ height: '44px', borderRadius: '8px', fontSize: '14px' }}
    >
      {isLoading && (
        <Spinner
          animation="border"
          variant="light"
          size="sm"
          className="me-2"
        />
      )}
      {t('reference.submitReferenceContacts')}
    </PrimaryButton>
  )
}

SubmitReferences.propTypes = {
  onSubmit: PropTypes.func.isRequired,
}

function ReferenceForm({ requiredReferences, onSubmit }) {
  const { t } = useTranslation()
  const [defaultValues, setDefaultValues] = useState(() => ({
    references: [{ ...EMPTY_REFERENCE }],
    consent: false,
  }))

  const valuesRef = useRef(defaultValues)
  const count = defaultValues.references.length

  const relationshipOptions = useMemo(
    () => [
      { value: '', label: t('reference.placeholder.selectRelationship') },
      {
        label: t('reference.relationship.directManager'),
        value: 'direct_manager',
      },
      { label: t('reference.relationship.teamLeader'), value: 'team_leader' },
      { label: t('reference.relationship.colleague'), value: 'colleague' },
      { label: t('reference.relationship.hr'), value: 'hr_representative' },
      { label: t('reference.relationship.client'), value: 'client' },
      { label: t('reference.relationship.other'), value: 'other' },
    ],
    [t],
  )

  const durationOptions = useMemo(
    () => [
      { value: '', label: t('reference.placeholder.selectDuration') },
      { label: t('reference.duration.less6'), value: 'less_6_months' },
      { label: t('reference.duration.6to12'), value: '6_12_months' },
      { label: t('reference.duration.1to2'), value: '1_2_years' },
      { label: t('reference.duration.2to5'), value: '2_5_years' },
      { label: t('reference.duration.more5'), value: '5_plus_years' },
    ],
    [t],
  )

  const schema = useMemo(
    () => referenceSchema(t, requiredReferences),
    [t, requiredReferences],
  )

  // Keep the latest form values so add/remove can preserve typed data.
  const handleFormWatch = useCallback((values) => {
    valuesRef.current = values
  }, [])

  const handleAddReference = () => {
    if (count >= MAX_REFERENCES) return
    const values = valuesRef.current
    setDefaultValues({
      references: [...(values.references || []), { ...EMPTY_REFERENCE }],
      consent: values.consent ?? false,
    })
  }

  const handleRemoveReference = (index) => {
    if (count <= 1) return
    const values = valuesRef.current
    const references = (values.references || defaultValues.references).slice()
    references.splice(index, 1)
    setDefaultValues({
      references,
      consent: values.consent ?? false,
    })
  }
  const inputs = useMemo(() => {
    const list = []
    for (let i = 0; i < count; i += 1) {
      const base = `references.${i}`
      list.push(
        {
          name: `${base}.fullname`,
          label: t('reference.fullName'),
          required: true,
          placeholder: t('reference.fullName'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.jobTitle`,
          label: t('reference.jobTitle'),
          required: true,
          placeholder: t('reference.jobTitle'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.companyName`,
          label: t('reference.companyName'),
          required: true,
          placeholder: t('reference.companyName'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.email`,
          label: t('reference.email'),
          required: true,
          type: 'email',
          placeholder: t('reference.email'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.phone`,
          label: t('reference.phone'),
          required: true,
          placeholder: t('reference.phone'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.candidateRelationship`,
          label: t('reference.relationshipToYou'),
          required: true,
          renderType: 'select',
          options: relationshipOptions,
          placeholder: t('reference.relationshipToYou'),
          width: 6,
          group: i,
        },
        {
          name: `${base}.workDuration`,
          label: t('reference.workDuration'),
          required: true,
          renderType: 'select',
          options: durationOptions,
          placeholder: t('reference.workDuration'),
          width: 6,
          group: i,
        },
      )
    }
    return list
  }, [count, t, relationshipOptions, durationOptions])

  return (
    <AbstractForm
      inputs={inputs}
      schema={schema}
      defaultValues={defaultValues}
      actions={[]}
      handleFormWatch={handleFormWatch}
      renderGroup={(index, fields) => (
        <ReferenceCard
          key={index}
          index={index}
          canRemove={count > 1}
          onRemove={() => handleRemoveReference(index)}
        >
          {fields}
        </ReferenceCard>
      )}
    >
      {count < MAX_REFERENCES && (
        <AddButton type="button" onClick={handleAddReference}>
          <Plus width="15" height="15" style={{ marginRight: '6px' }} />
          {t('reference.addAnother', { current: count, max: MAX_REFERENCES })}
        </AddButton>
      )}

      <ConsentField />

      <SubmitReferences onSubmit={onSubmit} />
    </AbstractForm>
  )
}

ReferenceForm.propTypes = {
  onSubmit: PropTypes.func,
  requiredReferences: PropTypes.number,
}

export default ReferenceForm
